/**
 * Serviço de Autenticação
 *
 * Funções para comunicação com a API de autenticação.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { api } from './api';
import { ApiResponse } from '@/types/common';
import { User } from '@/types/user';

/**
 * Resposta de login/registro
 */
interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

/**
 * Dados para login
 */
interface LoginData {
  email: string;
  password: string;
}

/**
 * Dados para registro
 */
export interface RegisterData {
  name: string;
  email: string;
  password: string;
  tenantName: string;
  tenantType: string;
  phone?: string;
  tenantDocument?: string;
  /** Plano escolhido no website (opcional) */
  planSlug?: string;
  acceptTerms: boolean;
}

/**
 * Resposta do cadastro (POST /auth/register). Não traz tokens: a tela entra em
 * seguida com o e-mail e a senha
 */
export interface RegisterResult {
  success: boolean;
  message: string;
  user: { id: string; email: string; name: string; role: string };
  tenant: { id: string; name: string; slug: string; type: string };
  subscription: { status: string; planName: string | null; endsAt: string; trialDays: number };
}

/**
 * Dados para reset de senha
 */
interface ResetPasswordData {
  token: string;
  password: string;
  confirmPassword: string;
}

/**
 * Realiza login
 *
 * @param data - Credenciais de login
 * @returns Dados de autenticação
 */
export async function login(data: LoginData): Promise<AuthResponse> {
  const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao fazer login');
  }

  return response.data.data;
}

/**
 * Realiza registro de novo usuário/estabelecimento
 *
 * @param data - Dados de registro
 * @returns Dados da conta criada (sem tokens)
 */
export async function register(data: RegisterData): Promise<RegisterResult> {
  const response = await api.post<RegisterResult>('/auth/register', data);
  return response.data;
}

/**
 * Realiza logout
 *
 * @param refreshToken - Refresh token para revogar
 */
export async function logout(refreshToken: string): Promise<void> {
  await api.post('/auth/logout', { refreshToken });
}

/**
 * Renova o access token
 *
 * @param refreshToken - Refresh token
 * @returns Novos tokens
 */
export async function refreshTokens(
  refreshToken: string,
): Promise<{ accessToken: string; refreshToken: string }> {
  const response = await api.post<
    ApiResponse<{ accessToken: string; refreshToken: string }>
  >('/auth/refresh', { refreshToken });

  if (!response.data.success) {
    throw new Error('Erro ao renovar token');
  }

  return response.data.data;
}

/**
 * Obtém dados do usuário atual
 *
 * @returns Dados do usuário
 */
export async function getCurrentUser(): Promise<User> {
  const response = await api.get<ApiResponse<User>>('/auth/me');

  if (!response.data.success) {
    throw new Error('Erro ao obter dados do usuário');
  }

  return response.data.data;
}

/**
 * Solicita reset de senha
 *
 * @param email - Email do usuário
 */
export async function forgotPassword(email: string): Promise<void> {
  const response = await api.post<ApiResponse<void>>('/auth/forgot-password', { email });

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao solicitar reset de senha');
  }
}

/**
 * Redefine a senha com token
 *
 * @param data - Token e nova senha
 */
export async function resetPassword(data: ResetPasswordData): Promise<void> {
  const response = await api.post<ApiResponse<void>>('/auth/reset-password', data);

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao redefinir senha');
  }
}

/**
 * Altera senha do usuário logado
 *
 * @param currentPassword - Senha atual
 * @param newPassword - Nova senha
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const response = await api.post<ApiResponse<void>>('/auth/change-password', {
    currentPassword,
    newPassword,
  });

  if (!response.data.success) {
    throw new Error(response.data.error?.message || 'Erro ao alterar senha');
  }
}

export default {
  login,
  register,
  logout,
  refreshTokens,
  getCurrentUser,
  forgotPassword,
  resetPassword,
  changePassword,
};
